<template>
	<div
		class="flex w-max max-w-full"
		:class="[isMine ? 'self-end' : 'self-start']"
	>
		<div class="flex space-x-4 items-end w-max max-w-full">
			<div
				class="w-max max-w-full md:max-w-prose shadow rounded-lg p-4"
				v-bind:class="isMine ? 'bg-green-200 justify-content-end' : 'bg-white'"
			>
				<div
					class="message"
					v-bind:class="{
						reject: event.type == 'REJECT' || event.type == 'CANCEL',
						accept: event.type == 'ACCEPT',
					}"
				>
					<div v-if="event.type == 'BID'">
						<div v-if="!bid.active" class="clearfix">
							<span class="btn pl-0"
								>{{ __(bid.meta.label) }}
								{{ __("is no longer up to date") }}</span
							>
						</div>

						<strong v-else>{{ __(bid.meta.label) }}</strong>

						<hr class="my-2" :class="isMine ? 'border-green-400' : ''" />
						<div
							v-bind:class="{
								'justify-content-end': isMine,
								'text-muted': !bid.meta.active,
							}"
						>
							<tradable-terms-summary
								:offer="bid"
								:reference="referenceTradable"
								class="text-sm"
								v-bind:extended="true"
							/>
						</div>
						<hr class="my-2" :class="isMine ? 'border-green-400' : ''" />
						<div
							class="alert alert-info"
							v-if="bid.extra.contract == 'EHB_CUSTOM'"
						>
							{{ __('Purchase conditions available under "More info"') }}
						</div>
						<crop-cost-warning
							:bid="bid"
							:meta="bid.meta"
							v-if="needsCostWarning"
						/>

						<div class="flex flex-wrap gap-x-4 gap-y-4 my-4">
							<ui-button
								v-for="action in bid.meta.actions"
								:key="action.id"
								class="flex-shrink-0"
								:type="getButtonClass(action.id)"
								v-on:click="doButtonAction(action.id)"
								:label="action.label"
								:disabled="disableButtons"
							/>
						</div>
					</div>

					<!-- contract -->
					<div
						v-if="event.type == 'CONTRACT'"
						v-bind:class="{ 'justify-content-end': isMine }"
					>
						<strong>{{ __("Contract") }}</strong>
						<hr class="my-2" :class="isMine ? 'border-green-400' : ''" />
						<tradable-terms-summary :offer="contract" v-bind:extended="true" />
						<hr class="my-2" :class="isMine ? 'border-green-400' : ''" />
						<div class="flex gap-2">
							<a
								v-bind:href="contractConfirmUrl"
								class="truncate bg-orange-500 text-white px-4 py-1.5 font-medium rounded-lg focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-orange-400 shadow hover:shadow-md hover:bg-orange-600 focus:outline-none font-medium"
								target="_blank"
								>{{ __("Contract confirmation") }} /
								{{ __("Closing certificate") }}</a
							>

							<a
								v-bind:href="contractPricingAppendixUrl"
								v-if="contractPricingAppendixUrl"
								class="truncate bg-orange-500 text-white px-4 py-1.5 font-medium rounded-lg focus:ring-2 focus:ring-orange-200 focus:ring-offset-2 focus:ring-offset-gray-50 focus:ring-orange-400 shadow hover:shadow-md hover:bg-orange-600 focus:outline-none font-medium"
								target="_blank"
								>{{ __("Pricing Appendix") }}
							</a>
						</div>
					</div>

					<div
						v-if="event.type == 'CONTRACT'"
						v-bind:class="{ 'justify-content-end': isMine, 'pt-4': true }"
					>
						<strong>{{ __("Contact information") }}</strong>
						<br />
						{{ event.counterpart.company_name }}
						<br />
						{{ event.counterpart.name }}
						<br />
						{{ event.counterpart.email }}
						<br />
						<span v-if="event.counterpart.tel_mobile">
							{{ __("Tel") }}: {{ event.counterpart.tel_mobile }}
							<br />
						</span>

						<span
							v-if="
								contract.seller_id == event.counterpart.id &&
								event.counterpart.sepa.iban != null
							"
						>
							<hr class="my-2" :class="isMine ? 'border-green-400' : ''" />
							<strong>{{ __("Bank account information") }}</strong>
							<br />
							{{ __("Bank account owner") }}:
							{{ event.counterpart.sepa.account_holder }}
							<br />
							IBAN: {{ event.counterpart.sepa.iban }}
							<br />
						</span>

						<span
							v-if="
								contract.seller_id == event.counterpart.id &&
								event.counterpart.eu_vatid != null
							"
						>
							<hr class="my-2" :class="isMine ? 'border-green-400' : ''" />
							<strong>{{ __("Tax details") }}</strong>
							<br />
							{{ __("Tax ID") }}: {{ event.counterpart.eu_vatid }}
							<br />
							<span v-if="event.counterpart.extra.de_farmer_vat_status"
								>{{ __("Taxation") }}: {{ deFarmerVatLabel }}</span
							>
						</span>
					</div>

					<hr
						class="my-2"
						:class="isMine ? 'border-green-400' : ''"
						v-if="
							event.message && (event.type == 'BID' || event.type == 'CONTRACT')
						"
					/>

					<!-- upload -->
					<div
						v-if="event.type == 'UPLOAD'"
						v-bind:class="{ 'justify-content-end': isMine }"
						class="flex"
					>
						<ui-icon
							class="h-5 w-5 mr-2 text-orange"
							name="file"
							stroke-width="2"
						/>
						<a
							class="text-orange font-semibold"
							v-bind:href="uploadLink"
							target="_blank"
						>
							{{ event.fileAttachment.filename }}
							<span v-if="event.fileAttachment.label_id"
								>({{ __(event.fileAttachment.label_id) }})</span
							>
							<span v-else-if="event.fileAttachment.label"
								>({{ event.fileAttachment.label }})</span
							>
						</a>
					</div>

					<div v-if="event.message != ''">
						<i
							class="icon icon-semi-stop-white"
							v-if="event.type == 'REJECT'"
						></i>
						<i
							class="icon icon-checkmark-white"
							v-if="event.type == 'ACCEPT'"
						></i>
						{{ event.message }}
					</div>
				</div>

				<ul
					class="flex flex-col space-y-3 gap-x-4"
					v-if="isLast && counterpartUploads.length > 0"
				>
					<li
						class="flex space-x-4 items-center"
						v-for="(upload, idx) in counterpartUploads"
						:key="'UPLOAD_' + idx"
					>
						<ui-icon
							class="h-5 w-5 text-gray-400 flex-shrink-0"
							name="file"
							stroke-width="2"
						/>
						<a
							target="_blank"
							class="underline"
							:href="$route('user_upload.download', { id: upload.id })"
							>{{ getUploadLabel(upload) }}</a
						>
					</li>
				</ul>

				<div
					v-if="isLast && hasLoadingInfo"
					v-bind:class="{ 'justify-content-end': isMine, 'pt-2': true }"
				>
					<ui-button
						type="primary"
						v-if="!showInfo"
						v-on:click="showInfo = !showInfo"
					>
						<!-- <ui-icon class="h-5 w-5" name="bell" stroke-width="2.5" />  -->
						{{ __("Loading and unloading details") }}
					</ui-button>
					<button
						v-if="showInfo"
						v-on:click="showInfo = !showInfo"
						class="hover:bg-gray-100 rounded-lg p-2 ml-auto font-semibold underline text-orange-500 text-lg flex gap-x-2 whitespace-nowrap items-center"
					>
						<ui-icon
							class="hidden md:block h-5 w-5"
							name="x"
							stroke-width="2.5"
						/>
					</button>

					<div v-if="showInfo">
						<span v-if="event.counterpart.extra.loading_schedule">
							{{ __("Loading times/Unloading times") }}:
							{{ event.counterpart.extra.loading_schedule }}
							<br />
						</span>
						<span v-if="event.counterpart.extra.loading_performance_hourly">
							{{ __("Loading rate/Unloading rate") }}:
							{{ event.counterpart.extra.loading_performance_hourly }}
							<br />
						</span>
						<span v-if="event.counterpart.extra.comment && event.type == 'BID'">
							{{ __("Additional information") }}:
							{{ event.counterpart.extra.comment }}
							<br />
						</span>
						<span
							v-if="
								event.counterpart.extra.fulfilment_info &&
								event.type == 'CONTRACT'
							"
						>
							{{ __("Processing information") }}:
							{{ event.counterpart.extra.fulfilment_info }}
							<br />
						</span>
						<span v-if="event.counterpart.extra.max_height_limited == 0">
							{{ __("Clearance") }}: {{ __("Unlimited") }}
							<br />
						</span>
						<span v-if="event.counterpart.extra.max_height_limited == 1">
							{{ __("Clearance") }}: {{ event.counterpart.extra.max_height }} m
							<br />
						</span>
						<span v-if="event.counterpart.extra.special_vehicles_only == 0">
							{{ __("Means of Transport") }}:
							{{ __("All vehicle types possible") }}
							<br />
						</span>
						<span v-if="event.counterpart.extra.special_vehicles_only == 1">
							{{ __("Means of Transport") }}:
							{{ __("Only the following vehicle types possible:") }}
							<span
								v-for="(row, idx) in event.counterpart.extra
									.special_vehicles_allowed"
								:key="idx"
								>{{ vehicleById(row) }},
							</span>
							<br />
						</span>
						<span v-if="event.counterpart.extra.has_scales == 1">
							{{ __("Weighing") }}: {{ __("Yes, weighing is possible") }}
							<br />
						</span>
					</div>
				</div>

				<div class="text-xs text-gray-500 mt-1 -mb-1 text-right truncate">
					<span v-if="isMine">{{ thread.my_name }}</span>
					<span v-else>{{ event.counterpart.company_name }}</span>
					<crop-timeago :date="event.created_at" />
				</div>

				<ui-modal
					ref="acceptModal"
					:small="true"
					:confirm-label="__('Accept')"
					:alert-style="'accept-offer'"
					v-on:confirm="confirmedButtonAction('accept')"
					v-on:dismiss="showAcceptModal = false"
					v-on:close="showAcceptModal = false"
					v-if="showAcceptModal"
					>{{
						bid.meta.label == "Offer from seller"
							? __("messages.accept_sell_offer")
							: __("messages.accept_buy_offer")
					}}</ui-modal
				>
				<ui-modal
					ref="rejectModal"
					:small="true"
					:confirm-label="__('Reject')"
					v-on:confirm="confirmedButtonAction('reject')"
					v-on:dismiss="showRejectModal = false"
					v-on:close="showRejectModal = false"
					v-if="showRejectModal"
					>{{
						__("messages.reject_offer", { args: { label: __(bid.meta.label) } })
					}}</ui-modal
				>
				<ui-modal
					ref="cancelModal"
					:small="true"
					:confirm-label="__('Cancel Offer')"
					v-on:confirm="confirmedButtonAction('cancel')"
					v-on:dismiss="showCancelModal = false"
					v-on:close="showCancelModal = false"
					v-if="showCancelModal"
					>{{
						__("messages.cancel_offer", { args: { label: __(bid.meta.label) } })
					}}</ui-modal
				>
			</div>
		</div>
	</div>
</template>

<script>
let options = require("../../cropOptions");

export default {
	props: {
		event: {
			type: Object,
			required: true,
		},
		thread: {
			type: Object,
			required: true,
		},
		disableButtons: {
			// in admin panel, disable action buttons
			type: Boolean,
			default: false,
		},
		isLast: {
			type: Boolean,
			required: false,
		},
		counterpartUploads: {
			type: Array,
			default: [],
		},
	},
	data() {
		return {
			bid: null,
			contract: null,
			showAcceptModal: false,
			showRejectModal: false,
			showCancelModal: false,
			test: null,
			showInfo: false,
			specialVehicleOptions: this.$cropOptions.specialVehicleOptions,
			user: this.$inertia.page.props.user,
		};
	},
	computed: {
		deFarmerVatLabel() {
			return (
				options.getLabel(
					this.event.counterpart.extra.de_farmer_vat_status,
					options.deFarmerVatStatusOptions
				) || this.__("N/A")
			);
		},
		isMine() {
			let senderId = this.event.sender_id,
				userId = this.user.id;

			if (senderId == userId) {
				return true;
			} else {
				return false;
			}
		},
		referenceTradable() {
			if (this.bid && this.bid.reference_tradable) {
				return this.bid.reference_tradable;
			} else {
				return null;
			}
		},
		needsCostWarning() {
			if (this.bid) {
				let canAccept =
					this.bid.meta.actions.filter((m) => m.id == "accept").length == 1;
				return canAccept; // if can accept bid, need cost warning!
			}
			return false;
		},
		acceptUrl: function () {
			return (
				window.baseUrl +
				"/api/thread/" +
				this.thread.id +
				"/event/" +
				this.event.id +
				"/accept"
			);
		},
		rejectUrl: function () {
			return (
				window.baseUrl +
				"/api/thread/" +
				this.thread.id +
				"/event/" +
				this.event.id +
				"/reject"
			);
		},
		cancelUrl: function () {
			return (
				window.baseUrl +
				"/api/thread/" +
				this.thread.id +
				"/event/" +
				this.event.id +
				"/cancel"
			);
		},
		contractConfirmUrl: function () {
			return (
				window.baseUrl + "/contract/" + this.contract.id + "/confirmation_pdf"
			);
		},
		contractPricingAppendixUrl: function () {
			if (this.contract.pricing_appendix_pdf) {
				return (
					window.baseUrl +
					"/contract/" +
					this.contract.id +
					"/pricing_appendix_pdf"
				);
			} else {
				return null;
			}
		},
		uploadLink() {
			return (
				window.baseUrl +
				"/api/user_upload/" +
				this.event.fileAttachment.id +
				"/download"
			);
		},
		hasLoadingInfo() {
			let i = this.event.counterpart.extra;
			return (
				i.loading_schedule ||
				i.loading_performance_hourly ||
				i.comment ||
				i.fulfilment_info
			);
		},
	},
	methods: {
		getUploadLabel(upload) {
			return this.__(upload.label_id ?? upload.label ?? upload.filename);
		},
		getButtonClass(actionId) {
			let map = {
				cancel: "secondary",
				renew: "primary",
				accept: "success",
				counter: "secondary",
				reject: "secondary",
			};
			return map[actionId];
		},
		vehicleById(id) {
			let vehicleLabel = null;
			this.specialVehicleOptions.forEach((vehicle) => {
				if (vehicle.id == id) {
					vehicleLabel = vehicle.label;
				}
			});
			return vehicleLabel;
		},
		doButtonAction(actionId) {
			this.test = actionId;
			if (actionId == "cancel") {
				this.showCancelModal = true;
			} else if (actionId == "renew") {
				//window.location = window.baseUrl + "/bid/counter/" + this.bid.id;
				let bidLink = this.$route("bid.create_counter", {
					bidId: this.bid.id,
				});
				return this.$bus.$emit("toggle-iframe-modal", bidLink);
			} else if (actionId == "accept") {
				//this.postEvent(this.acceptUrl); // modal?
				this.showAcceptModal = true;
			} else if (actionId == "counter") {
				//window.location = window.baseUrl + "/bid/counter/" + this.bid.id;
				let bidLink = this.$route("bid.create_counter", {
					bidId: this.bid.id,
				});
				return this.$bus.$emit("toggle-iframe-modal", bidLink);
			} else if (actionId == "reject") {
				//this.postEvent(this.rejectUrl);
				this.showRejectModal = true;
			}
		},
		confirmedButtonAction(actionId) {
			if (actionId == "accept") {
				this.showAcceptModal = false;
				this.postEvent(this.acceptUrl, {
					bid: this.bid,
				});
			} else if (actionId == "reject") {
				this.showRejectModal = false;
				this.postEvent(this.rejectUrl);
			} else if (actionId == "cancel") {
				this.showCancelModal = false;
				this.postEvent(this.cancelUrl);
			}
		},
		postEvent(url, data = {}) {
			let that = this;
			axios
				.post(url, {
					data: data,
				})
				.then(function (response) {
					that.message = "";
					console.log("response", response);
					that.$bus.$emit("thread-update", response.data.data);
				})
				.catch(function (e) {
					let msg = e.response.data ? e.response.data.message : "Error!";
					alert(msg);
				});
		},
		async pollBidUpdates() {
			let that = this;
			while (false) {
				// TEMP workaround?
				try {
					let resp = await axios.get(
						window.baseUrl + "/api/bid/watch/" + that.bid.id,
						{
							params: {
								updated_at: that.bid.updated_at,
							},
						}
					);
					if (resp.data.data) {
						that.bid = resp.data.data;
					}
				} catch (e) {
					console.log(e);
				} finally {
					await new Promise((r) => setTimeout(r, 500));
				}
			}
		},
	},
	watch: {
		thread: {
			handler() {
				let bid = null,
					contract = null,
					referenceTradable = null,
					that = this;

				if (this.event.type == "BID") {
					this.thread.bids.forEach(function (b) {
						if (b.id == that.event.bid_id) {
							bid = b;
						}
					});
				}

				if (this.event.type == "CONTRACT") {
					this.thread.contracts.forEach(function (c) {
						if (c.id == that.event.contract_id) {
							contract = c;
						}
					});
				}

				this.bid = bid;
				this.contract = contract;
			},
			deep: true,
			immediate: true,
		},
	},
	mounted() {
		if (this.bid) {
			if (this.bid.meta.active) {
				this.pollBidUpdates();
				console.log("WATCHING", this.bid.id);
			} else {
				console.log("NOT WATCHING", this.bid.id);
			}
		}
	},
};
</script>
